//
// Copyright (c) 2011-2019 by Curalate, Inc.
//

import moment from 'moment-timezone';

import { FeatureStorageScope, IStorageValue } from '@utils/featureStorageModel';

const storage: Storage = localStorage;

const STORAGE_KEY_ROOT = 'crl8.feature-storage';
const STORAGE_KEY = (scope: FeatureStorageScope) =>
  `${STORAGE_KEY_ROOT}.${scope}`;

interface ISetFeatureOptions {
  value: any;
  overwrite?: boolean;
  expires?: Date;
}

/**
 * Stores a scoped value in localStorage. This can be
 * used to track user interactions with certain features and change
 * the experience based on previous interactions. The most basic
 * example is showing/hiding a "New" badge in the UI based on whether
 * or not a user has interacted with a new feature. By storing a record
 * of this interaction in localStorage, you can hide the "New" badge
 * the next time the component would otherwise be rendered.
 *
 * Set the feature value unless there's an existing value
 * already stored, and override was not set to 'true'
 *
 * @param scope Name of the scoped action
 * @param value The value to store
 * @param overwrite If true, overwrite the existing value (if one exists)
 * @param expires The date after which the value will be removed from storage
 * @param stopSettingOn The date after which we will no longer save the value in storage
 */
export const setFeatureValue = (
  scope: FeatureStorageScope,
  options: ISetFeatureOptions,
): void => {
  clearAllExpiredValues();

  const { overwrite, value } = options;
  const expires = options.expires || aYearFromNow;

  const existingStorageValue = get(scope);

  if (!existingStorageValue || overwrite) {
    const storageValue = { value, expires };
    set(scope, storageValue);
  }
};

/**
 * Get the feature value if it exists, unless the expires
 * field has passed, in which case remove the value and
 * return null
 *
 * @param feature Name of the feature
 * @param action The action being taken
 */
export const getFeatureValue = <T = any>(
  scope: FeatureStorageScope,
): T | undefined => {
  clearAllExpiredValues();

  const storageValue = get(scope);
  return storageValue?.value ? (storageValue.value as T) : undefined;
};

const momentHasPassed = (expires: Date): boolean => {
  return moment().isAfter(expires);
};

const clearAllExpiredValues = (): void => {
  const allScopes = getAllScopes();
  allScopes.forEach((scope) => {
    const storageValue = get(scope);
    if (storageValue && momentHasPassed(storageValue.expires)) {
      remove(scope);
    }
  });
};

const getAllScopes = (): FeatureStorageScope[] => {
  return Object.keys(storage)
    .filter((key) => key.startsWith(STORAGE_KEY_ROOT))
    .map(
      (key) =>
        key.substring(
          STORAGE_KEY_ROOT.length + 1,
          key.length,
        ) as FeatureStorageScope,
    );
};

const get = (scope: FeatureStorageScope): IStorageValue | undefined => {
  const stringifiedValue = storage.getItem(STORAGE_KEY(scope));
  return stringifiedValue
    ? (JSON.parse(stringifiedValue) as IStorageValue)
    : undefined;
};

const set = (scope: FeatureStorageScope, value: IStorageValue) => {
  const stringifiedValue = JSON.stringify(value);
  storage.setItem(STORAGE_KEY(scope), stringifiedValue);
};

const remove = (scope: FeatureStorageScope): void => {
  storage.removeItem(STORAGE_KEY(scope));
};

const aYearFromNow: Date = (() => {
  const d = new Date();
  d.setFullYear(d.getFullYear() + 1);
  return d;
})();
